import React, { useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import ReactTooltip from 'react-tooltip';
import S from './style';

import { GlobalContext } from '../../context/GlobalState';
import StatusBadge from '../StatusBadge';
import Avatar from '../Avatar';
import { getUsersById } from '../../services/users';
import { HostPlatform } from '../../config/hostPlatform';
import CopyToClipboard from './CopyToClipboard';
import { ActionTooltip } from './ActionTooltip';

const DATE_FORMAT = 'dd/MMM/yyyy';

const mapOptionsSelect = (option) => ({
  name: option.name,
  photo_thumb: option.photo_tiny || null,
});

const MAX_PARTICIPANTS_TO_SHOW = 3;
function useRetrospectiveParticipants(retrospective) {
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    (async () => {
      const allParticipants = new Set([
        retrospective.facilitator,
        ...retrospective.participants,
      ]);
      const usersFounded = await getUsersById([...allParticipants.values()]);
      const allUsers = usersFounded.map((user) => mapOptionsSelect(user));
      setParticipants(allUsers);
    })();
  }, [retrospective]);

  return participants;
}

function RetrospectiveCard({
  users,
  retrospective,
  onClick,
  onDelete,
  onEdit,
  onDuplicate,
}) {
  const { user } = useContext(GlobalContext);
  const participants = useRetrospectiveParticipants(retrospective);
  const [copied, setCopied] = useState(false);

  const createdOn = retrospective.createdOn.seconds
    ? retrospective.createdOn.toDate()
    : new Date(retrospective.createdOn);

  const date = format(createdOn, DATE_FORMAT);
  const isAzure = HostPlatform.isAzure();
  const isJira = HostPlatform.isJira();
  const userCard = users.find((user) => user.id === retrospective.createdBy);

  const copyUrlIsAvailable = isJira || isAzure;

  const userName = userCard ? userCard.name : 'User not found';
  const canEdit =
    user.id === retrospective.facilitator ||
    user.id === retrospective.createdBy;

  const handleEdit = (event) => {
    event.stopPropagation();
    if (canEdit) onEdit(retrospective);
  };
  const handleCopyUrl = (value) => setCopied(value);
  const handleDelete = (event) => {
    event.stopPropagation();
    if (canEdit) onDelete();
  };

  return (
    <S.Card onClick={onClick}>
      <S.Title>{retrospective.name}</S.Title>
      <div>
        <S.Author>{userName}</S.Author>
        <S.Date>{date}</S.Date>
        <S.AvatarGroup data-tip data-for={retrospective.id} group>
          {participants.slice(0, MAX_PARTICIPANTS_TO_SHOW).map((u, index) => (
            <Avatar
              key={`${retrospective.id}-${u.name}`}
              border
              size="sm"
              order={index}
              group
              src={u?.photo_thumb}
              color="bgCard"
            />
          ))}

          {participants.length > MAX_PARTICIPANTS_TO_SHOW && (
            <Avatar
              defaultValue={`+${
                participants.length - MAX_PARTICIPANTS_TO_SHOW
              }`}
              order={8}
              group
              size="sm"
              border
              color="bgCard"
            />
          )}
          <ReactTooltip
            id={retrospective.id}
            place="left"
            effect="solid"
            delayShow={300}
            border
          >
            <S.TooltipContent>
              {participants.map(
                (p) =>
                  p &&
                  p.name && (
                    <p key={p.name + participants.toString()}>{p.name}</p>
                  )
              )}
            </S.TooltipContent>
          </ReactTooltip>
        </S.AvatarGroup>
      </div>

      <S.IconsHover onClick={(event) => event.stopPropagation()}>
        <S.DuplicateIcon
          data-tip
          data-for={`duplicateTooltip-${retrospective.id}`}
          onClick={onDuplicate}
        />
        <S.EditIcon
          data-tip
          data-for={`editTooltip-${retrospective.id}`}
          onClick={handleEdit}
          disabled={!canEdit}
        />
        <S.DeleteIcon
          data-tip
          data-for={`deleteTooltip-${retrospective.id}`}
          onClick={handleDelete}
          disabled={!canEdit}
        />

        {copyUrlIsAvailable && (
          <CopyToClipboard
            id={retrospective.id}
            handleCopyUrl={handleCopyUrl}
            copied={copied}
          />
        )}
      </S.IconsHover>

      {copyUrlIsAvailable && (
        <ReactTooltip
          id={`linkto-${retrospective.id}`}
          place="top"
          effect="solid"
          border
        >
          <S.TooltipContent>
            {copied ? 'Copied!' : 'Copy link to retrospective'}
          </S.TooltipContent>
        </ReactTooltip>
      )}

      <StatusBadge retrospective={retrospective} />

      <ActionTooltip
        id={`duplicateTooltip-${retrospective.id}`}
        isForbidden={false}
      >
        Duplicate retrospective
      </ActionTooltip>
      <ActionTooltip
        id={`editTooltip-${retrospective.id}`}
        isForbidden={!canEdit}
      >
        Edit retrospective
      </ActionTooltip>
      <ActionTooltip
        id={`deleteTooltip-${retrospective.id}`}
        isForbidden={!canEdit}
      >
        Delete retrospective
      </ActionTooltip>
    </S.Card>
  );
}

export default RetrospectiveCard;
